import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import Archive from '@mui/icons-material/Archive';
import Clear from '@mui/icons-material/Clear';
import Edit from '@mui/icons-material/Edit';
import GetApp from '@mui/icons-material/DownloadOutlined';
import Unarchive from '@mui/icons-material/Unarchive';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Link, useHistory } from 'react-router-dom';
import { GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import {
  getAllCadidatSelections,
  deleteCandidatSelection,
  archiveCandidatSelection,
  exportCandidatsSelection,
} from '../../services/SelectionCandidatService';
import moment from 'moment';
import { withSnackbar } from '../../utils/SnackbarHOC';
import { updateObjectStateValue, getUserData } from '../../utils/FunctionHelpers';
import SelectionModal from '../Dashboard/Modals/SelectionModal';
import DialogModal from '../../utils/DialogModal';
import fileDownload from 'js-file-download';
import { commonGridSx, gridContainerSx, gridLocaleText } from '../../utils/gridStyles';
import { DataGrid } from '../../components/SortableDataGrid/SortableDataGrid';

const ListSelections = ({ snackbarShowMessage, history: histProp }) => {
  const history = useHistory();
  const role = getUserData().role;
  const [reloadSelections, setReloadSelections] = useState(false);
  const [selectionsData, setSelectionData] = useState({ isLoading: false, data: [], archived: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    updateObjectStateValue(setSelectionData, { isLoading: true, data: [] });
    getAllCadidatSelections(`?archived=${selectionsData.archived}`)
      .then(({ data }) => updateObjectStateValue(setSelectionData, { isLoading: false, data: data.candidatsSelections }))
      .catch(() => snackbarShowMessage('Erreur de chargement des sélections.', 'error', 4000));
    return () => updateObjectStateValue(setSelectionData, { isLoading: false, data: [] });
  }, [reloadSelections]);

  const handleDeleteCandidatSelection = ({ selection }) => {
    setLoading(true);
    snackbarShowMessage('Suppression de la sélection en cours...', 'warning');
    deleteCandidatSelection(selection)
      .then(() => { snackbarShowMessage('La sélection a été supprimée.', 'success', 4000); setReloadSelections(!reloadSelections); })
      .catch(() => snackbarShowMessage('Erreur de suppression de la sélection.', 'error', 2000))
      .finally(() => setLoading(false));
  };

  const handleArchiveCandidatSelection = (selection, isArchived) => {
    snackbarShowMessage(`${isArchived ? 'Archivage' : 'Désarchivage'} de la sélection en cours...`, 'warning');
    archiveCandidatSelection(selection, { isArchived })
      .then(() => { snackbarShowMessage(`La sélection a été ${isArchived ? 'archivée' : 'désarchivée'} avec succès.`, 'success'); setReloadSelections(!reloadSelections); })
      .catch(() => snackbarShowMessage(`Erreur ${isArchived ? "d'archivage" : 'de désarchivage'} de la sélection.`, 'error', 4000));
  };

  const handleShowArchivedSelection = () => {
    updateObjectStateValue(setSelectionData, { archived: !selectionsData.archived });
    setReloadSelections(!reloadSelections);
  };

  const handleDownload = (selectionId, selectionName, rowId) => {
    const updated = selectionsData.data.map((s) => s.id === rowId ? { ...s, isExporting: true } : s);
    updateObjectStateValue(setSelectionData, { data: updated });
    exportCandidatsSelection(selectionId)
      .then(({ data }) => fileDownload(data, selectionName + '-export.xlsx'))
      .catch(() => snackbarShowMessage("Erreur d'export de la sélection.", 'error', 2000))
      .finally(() => {
        const reset = selectionsData.data.map((s) => s.id === rowId ? { ...s, isExporting: false } : s);
        updateObjectStateValue(setSelectionData, { data: reset });
      });
  };

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f1f5f9', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', mr: 'auto' }}>Mes Sélections</Typography>
      <Button startIcon={selectionsData.archived ? <RemoveIcon /> : <AddIcon />} onClick={handleShowArchivedSelection}
        sx={{ textTransform: 'none', fontWeight: 500, fontSize: '1rem', color: '#03407b', border: '1px solid #aed6f1', borderRadius: 2, px: 2, py: 0.75, background: '#ebf4fb', '&:hover': { background: '#d6eaf8', borderColor: '#7fb3d3' } }}>
        {selectionsData.archived ? 'Masquer archivées' : 'Voir archivées'}
      </Button>
      <GridToolbarQuickFilter
        sx={{
          '& .MuiInputBase-root': { borderRadius: 2, fontSize: '1rem', background: '#f8fafc', px: 1.5, height: 36 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
          '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7fb3d3' },
          '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#03407b' },
        }}
        placeholder="Rechercher..."
        debounceMs={400}
      />
      <GridToolbarColumnsButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarFilterButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarDensitySelector sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
    </GridToolbarContainer>
  );

  const iconBtnSx = (color, bg, hoverBg) => ({
    color,
    background: bg,
    borderRadius: '8px',
    p: 0.75,
    '&:hover': { background: hoverBg },
  });

  const columns = [
    {
      field: 'dateCreation', headerName: "DATE D'AJOUT", minWidth: 120, flex: 0.8,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value?.date ? moment(p.value.date).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'selectionName', headerName: 'NOM SÉLECTION', minWidth: 180, flex: 1.4,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'nom_mission', headerName: 'MISSION', minWidth: 160, flex: 1.4,
      renderCell: (p) => p.value ? (
        <Link to={{ pathname: `/societe/${p.row.societeId}/missions/${p.row.missionId}/view` }}
          style={{ textDecoration: 'none', color: '#0ea5e9', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value}
        </Link>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'creator', headerName: 'TALENT MANAGER', minWidth: 150, flex: 1,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'totalCandidats', headerName: 'NBR', minWidth: 70, flex: 0.4, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Link
          to={{ pathname: `/selections/${p.row.id}/candidats/list`, search: `?selectionName=${p.row.selectionName}&consultant=${p.row.creator}` }}
          onClick={() => window.localStorage.setItem('data_vivier_selected', JSON.stringify({
            pathname: `/selections/${p.row.id}/candidats/list`,
            search: `?selectionName=${p.row.selectionName}&quantity=${p.value}&consultant=${p.row.creator}`,
          }))}
          style={{ textDecoration: 'none' }}>
          <Chip label={p.value ?? 0} size="small" sx={{ background: '#ebf4fb', color: '#03407b', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }} />
        </Link>
      ),
    },
    {
      field: 'isArchived', headerName: 'ARCHIVÉE', minWidth: 100, flex: 0.6, align: 'center', headerAlign: 'center',
      renderCell: (p) => p.value
        ? <Chip label="Archivée" size="small" sx={{ background: '#fef9c3', color: '#854d0e', fontSize: '0.7rem', fontWeight: 600, height: 20 }} />
        : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    ...(!role.includes('ROLE_DELIVERY_MANAGER') ? [{
      field: 'actions_col', headerName: 'ACTIONS', minWidth: 130, flex: 0.8, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, width: '100%' }}>
          <SelectionModal
            oldSelectionName={p.row.selectionName}
            oldCreator={{ id: p.row.creatorId, value: p.row.creator }}
            oldMission={p.row.missionId ? { id: p.row.missionId, value: p.row.nom_mission } : ''}
            idSelection={p.row.id}
            setReloadSelections={setReloadSelections}
            reloadSelections={reloadSelections}
            isModify={true}
            snackbarShowMessage={snackbarShowMessage}
          >
            {(handleOpen) => (
              <Tooltip title="Modifier" placement="top" arrow>
                <IconButton onClick={handleOpen} size="small" sx={iconBtnSx('#92400e', '#fefce8', '#fef9c3')}>
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </SelectionModal>
          {getUserData().role.includes('ROLE_DIRECTION') && (
            <Tooltip title={p.row.isArchived ? 'Désarchiver' : 'Archiver'} placement="top" arrow>
              <IconButton size="small"
                onClick={() => handleArchiveCandidatSelection(p.row.id, !p.row.isArchived)}
                sx={iconBtnSx(p.row.isArchived ? '#991b1b' : '#1e40af', p.row.isArchived ? '#fef2f2' : '#ebf4fb', p.row.isArchived ? '#fee2e2' : '#dbeafe')}>
                {p.row.isArchived ? <Unarchive sx={{ fontSize: 16 }} /> : <Archive sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          )}
          {role.includes('ROLE_DIRECTION') && (
            <DialogModal
              label={
                <Tooltip title="Supprimer" placement="top" arrow>
                  <IconButton size="small" sx={iconBtnSx('#991b1b', '#fef2f2', '#fee2e2')}>
                    <Clear sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              }
              description="Voulez vous supprimer la sélection?"
              title="Supprimer la sélection?"
              closeLabel="Annuler"
              confirmLabel="Confirmer"
              loading={loading}
              onConfirm={handleDeleteCandidatSelection}
              data={{ selection: p.row.id }}
            />
          )}
          {getUserData().role.includes('ROLE_DIRECTION') && (
            <Tooltip title="Exporter" placement="top" arrow>
              <span>
                <IconButton size="small" disabled={p.row.isExporting}
                  onClick={() => handleDownload(p.row.id, p.row.selectionName, p.row.id)}
                  sx={iconBtnSx('#166534', '#f0fdf4', '#dcfce7')}>
                  {p.row.isExporting ? <CircularProgress size={14} /> : <GetApp sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ),
    }] : []),
  ];

  const rows = (selectionsData.data || []).map((s) => ({ ...s, isExporting: s.isExporting ?? false }));

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 1, md: 1.5 }, pb: 2 }}>
      <Box sx={{ ...gridContainerSx, height: 'calc(100vh - 110px)', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={selectionsData.isLoading}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
    </Box>
  );
};

export default withSnackbar(ListSelections);
